import { useEffect, useMemo, useState, useCallback } from "react";

import {
  Banner,
  Button,
  LoadingSpinner,
  useModal,
  RefreshSVG,
  PlusSVG,
} from "@newsela/angelou";
import { isEmpty, some } from "@s-libs/micro-dash";
import moment from "moment";
import { useRouter } from "next/router";

import useStudentSearchRestrictionsGet from "@/admin/hooks/useStudentSearchRestrictionsGet";
import getListOfGradesInContentSettingsGroups from "@/admin/utils/getListOfGradesInContentSettingsGroups/getListOfGradesInContentSettingsGroups";
import sortGradeGroups from "@/admin/utils/sortGradeGroups";
import { ApiCatalog, useApiQuery } from "@/api";
import globalToasterRef from "@/base/globalToasterRef";
import ClassroomLastSyncFailedBanner from "@/classrooms/ClassroomLastSyncFailedBanner";
import ClassroomSwitchSyncModal from "@/classrooms/ClassroomSwitchSyncModal";
import ClassroomSyncModal from "@/classrooms/ClassroomSyncModal";
import CreateNewClassroomModal from "@/classrooms/CreateNewClassroomModal";
import useClassroomRosterChoiceGet from "@/classrooms/useClassroomRosterChoiceGet";
import useClassroomRosterChoicePost from "@/classrooms/useClassroomRosterChoicePost";
import useClassroomRosterSyncFollowUpGet from "@/classrooms/useClassroomRosterSyncFollowUpGet";
import DirectoryTable from "@/directory/DirectoryTable";
import useDistrictBulkProcessingConfigGet from "@/district/hooks/useDistrictBulkProcessingConfigGet";
import SettingsBasePage from "@/settings/SettingsBasePage";
import getUserProperties from "@/users/getUserProperties";
import UserClassroomDirectoryTable from "@/users/UserClassroomDirectoryTable";
import useUser from "@/users/useUser";
import useUserClassroomGet from "@/users/useUserClassroomGet";
import getAuthProviderName from "@/utils/getAuthProviderName";
import LearnAboutClassRoster from "../LearnAboutClassRoster";
import SyncClassesFromLMSInstructions from "./SyncClassesFromLMSInstructions";
import ClassroomCreatedMessage from "./ClassroomCreatedMessage";
import {
  actionsContainerCss,
  pageDescriptionCss,
  bannerForRelayingStudentSearchBlockCss,
  buttonContentAlignmentCSS,
  lastSyncTextWidth,
  classSyncLoadingSpinner,
} from "./ClassesSettingsPage.style";
import {
  EVENT_TOAST_CLASS_CREATED,
  EVENT_TOAST_CLASS_SYNCED,
  PAGE_SIZE,
  SETTINGS_BASE_PAGE_PROPS,
  EVENT_TOAST_ROSTER_CHOICE_FAIL,
} from "./ClassesSettingsPage.constants";

const ClassesSettingsPage = () => {
  const router = useRouter();
  const { data: user } = useUser();
  const {
    canCreateClassrooms,
    canSyncClassrooms,
    hasBulkClassrooms,
    isAuthorizedPrincipal,
    isAuthorizedTeacher,
  } = getUserProperties(user);
  const [
    {
      syncing,
      syncingCompleted,
      initialLoading: loadingRosterSync,
      data: rosterSync = {},
    },
    followUpRosterSync,
  ] = useClassroomRosterSyncFollowUpGet(user.auth_type, {
    skip: !canSyncClassrooms,
  });

  const [
    { data: rosterChoice = {}, loading: loadingRosterChoice },
    setRosterChoice,
  ] = useClassroomRosterChoiceGet();

  const {
    data: { results: classrooms = [], count = 0 } = {},
    loading: loadingClasses,
    controls,
    fetchClassrooms,
  } = useUserClassroomGet(user.id, { hide_archived: false }, PAGE_SIZE);

  const {
    data: studentSearchRestrictions,
    loading: loadingStudentSearchRestrictions,
  } = useStudentSearchRestrictionsGet();

  const [postRosterChoice, { loading: isSavingRosterChoice }] =
    useClassroomRosterChoicePost();

  const {
    data: districtBulkProcessingConfig = [],
    loading: loadingDistrictBulkConfig,
  } = useDistrictBulkProcessingConfigGet();

  const {
    error: errorRosterClassrooms,
    data: rosterClassrooms,
    isLoading: isLoadingRosterClassrooms,
  } = useApiQuery({
    apiKey: ApiCatalog.rosterClassroom.key,
    apiSettings: {
      queryParams: {
        provider: user.auth_type,
      },
    },
    tanstackOptions: {
      enabled: !!user,
      retry: false,
    },
  });

  const [
    showBannerRelayingStudentSearchBlock,
    setShowBannerRelayingStudentSearchBlock,
  ] = useState(false);
  const [hasArchivedClasses, setHasArchivedClasses] = useState(false);
  const [createClassModalTriggerEl, setCreateClassModalTriggerEl] =
    useState(null);
  const [syncClassModalTriggerEl, setSyncClassModalTriggerEl] = useState(null);
  const [
    syncClassModalProps,
    setSyncClassModalActive,
    syncClassModalTriggerRef,
  ] = useModal(false);
  const [
    createClassModalProps,
    setCreateClassModalActive,
    createClassModalTriggerRef,
  ] = useModal(false);
  const [
    switchSyncModalProps,
    setSwitchSyncModalActive,
    switchSyncModalTriggerRef,
  ] = useModal(false);
  const [isAlertClosed, setIsAlertClosed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isIntegrationUser = user.auth_type !== "forms";
  const isMyRosterChoiceProvider = user.auth_type === rosterChoice.provider;
  const hasLastSyncFailed =
    isMyRosterChoiceProvider && rosterSync.has_failures && !syncing;
  const isLoading =
    loadingRosterSync ||
    loadingRosterChoice ||
    loadingStudentSearchRestrictions ||
    loadingDistrictBulkConfig;
  const shouldShowSyncClassesFromLMSInstructions =
    hasBulkClassrooms && !canSyncClassrooms;
  const bulkType = districtBulkProcessingConfig?.[0]?.roster_provider;
  const cameFromAssignmentPage =
    Object.hasOwn(router.query, "cameFromAssignmentPage") &&
    !!router.query?.next;

  useEffect(() => {
    const isTeacherOrPrincipal = isAuthorizedTeacher || isAuthorizedPrincipal;

    if (studentSearchRestrictions && isTeacherOrPrincipal) {
      // If at least one of the admin's content-settings configurations
      // (configurations created by either the user or their district/school
      // superiors) have the student-search functionality disabled, display the
      // banner that relays that information.
      const studentSearchIsBlockedAndAlertUser = some(
        studentSearchRestrictions,
        (restriction) => {
          return restriction.search_status_disabled;
        },
      );

      if (studentSearchIsBlockedAndAlertUser) {
        setShowBannerRelayingStudentSearchBlock(true);
      }
    }
  }, [loadingStudentSearchRestrictions]);

  const getSyncSuccessToastMessage = () => {
    if (!hasArchivedClasses) {
      return `Synced from ${getAuthProviderName(rosterChoice.provider)}`;
    }
    const isOneRoster = districtBulkProcessingConfig?.some(
      (config) => config.roster_provider === "oneroster",
    );
    if (isOneRoster) {
      return "District rostered classes archived and new classes are syncing";
    }
    return `${getAuthProviderName(
      bulkType,
    )} classes archived and new classes are syncing`;
  };

  useEffect(() => {
    if (
      !cameFromAssignmentPage ||
      isLoadingRosterClassrooms ||
      syncingCompleted
    ) {
      return;
    }
    // The class sync modal can only be opened if both the user feature and the list of valid classes are available.
    // FIXME: If the user has been signed in via a provider (e.g., Google) for too long, the session may expire,
    // potentially resulting in a 401 error and an empty rosterClassrooms list.
    const shouldOpenClassSyncModal =
      canSyncClassrooms &&
      cameFromAssignmentPage &&
      (rosterClassrooms?.filter(({ is_synced: isSynced }) => !isSynced)
        ?.length ?? 0) > 0;
    if (shouldOpenClassSyncModal) {
      syncClassModalTriggerEl?.click();
      return;
    }
    createClassModalTriggerEl?.click();
  }, [
    cameFromAssignmentPage,
    createClassModalTriggerEl,
    syncClassModalTriggerRef,
    isLoadingRosterClassrooms,
    syncingCompleted,
  ]);

  useEffect(() => {
    if (!syncingCompleted) return;
    if (!hasLastSyncFailed) {
      globalToasterRef.current.displayToast({
        ...EVENT_TOAST_CLASS_SYNCED,
        children: getSyncSuccessToastMessage(),
      });
      if (cameFromAssignmentPage) {
        setIsRedirecting(true);
        router.push(router.query.next);
        return;
      }
    }
    fetchClassrooms();
  }, [syncingCompleted, cameFromAssignmentPage]);

  const createClassroomModalNewClassButtonRef = useCallback((el) => {
    setCreateClassModalTriggerEl(el);
    createClassModalTriggerRef.current = el;
  }, []);

  const syncClassroomModalNewSyncClassButtonRef = useCallback((el) => {
    setSyncClassModalTriggerEl(el);
    syncClassModalTriggerRef.current = el;
  }, []);

  const getFormattedGradesWithStudentSearchDisabled = () => {
    const configurationsWithStudentSearchDisabled =
      studentSearchRestrictions?.filter((searchRestriction) => {
        return searchRestriction.search_status_disabled;
      });
    const sortedConfigurations = sortGradeGroups(
      configurationsWithStudentSearchDisabled,
    );
    const formattedGradeRanges =
      getListOfGradesInContentSettingsGroups(sortedConfigurations);

    return formattedGradeRanges.replace(/[0]/i, "K");
  };

  const onNewClassroom = (classroom) => {
    globalToasterRef.current.displayToast({
      ...EVENT_TOAST_CLASS_CREATED,
      children: <ClassroomCreatedMessage classroom={classroom} />,
    });
    if (cameFromAssignmentPage) {
      setIsRedirecting(true);
      router.push(router.query.next);
      return;
    }
    setCreateClassModalActive(false);
    router.push(`/account/classes/${classroom.id}/`);
  };

  const onSyncClassroom = (rosterSync, archivedClasses) => {
    setIsAlertClosed(false);
    setHasArchivedClasses(archivedClasses);
    followUpRosterSync(rosterSync);
  };

  const onSwitchSync = (rosterChoice) => {
    setRosterChoice(rosterChoice);
    setSyncClassModalActive(canSyncClassrooms);
  };

  const onClickSwitchSync = async () => {
    if (isEmpty(rosterChoice)) {
      const response = await postRosterChoice({ provider: user.auth_type });
      if (response.error) {
        globalToasterRef.current.displayToast(EVENT_TOAST_ROSTER_CHOICE_FAIL);
      } else {
        setRosterChoice(response.data);
        setSyncClassModalActive(canSyncClassrooms);
      }
    } else {
      setSwitchSyncModalActive(true);
    }
  };

  const renderPageDescription = () => {
    const shouldShowLastSyncTime = !!rosterSync.time_completed;

    if (!shouldShowLastSyncTime && !shouldShowSyncClassesFromLMSInstructions) {
      return null;
    }

    return (
      <div css={pageDescriptionCss}>
        {shouldShowLastSyncTime && (
          <span css={lastSyncTextWidth}>
            {`Last sync from ${getAuthProviderName(rosterSync.provider)}
          on ${moment(rosterSync.time_completed).format("MM/DD/yyyy HH:mm")}`}
          </span>
        )}
        {shouldShowSyncClassesFromLMSInstructions && (
          <SyncClassesFromLMSInstructions />
        )}
      </div>
    );
  };

  const renderAlertBanner = () => {
    if (!hasLastSyncFailed || isAlertClosed) return;
    return (
      <ClassroomLastSyncFailedBanner
        provider={rosterSync.provider}
        onClose={() => setIsAlertClosed(true)}
      />
    );
  };

  const renderSaveRosterChoice = () => {
    const authProviderName = getAuthProviderName(user.auth_type);
    const syncButtonProps = {
      flavor: Button.flavors.secondary,
      icon: { SvgComponent: RefreshSVG, alignment: Button.iconAlignments.left },
    };
    return (
      <>
        <Button
          {...syncButtonProps}
          ref={switchSyncModalTriggerRef}
          onClick={onClickSwitchSync}
          disabled={isSavingRosterChoice}
          __cssFor={buttonContentAlignmentCSS}
          dataProps={{
            "data-testid": "set-roster-choice-btn",
          }}
        >
          {isSavingRosterChoice
            ? "Connecting..."
            : `Sync from ${authProviderName}`}
        </Button>
        <ClassroomSwitchSyncModal
          modalProps={switchSyncModalProps}
          setActive={setSwitchSyncModalActive}
          user={user}
          rosterChoice={rosterChoice}
          onRosterChoiceChanged={onSwitchSync}
        />
      </>
    );
  };

  const renderConnectedToProvider = () => {
    const authProviderName = getAuthProviderName(user.auth_type);
    const syncButtonProps = {
      flavor: Button.flavors.secondary,
      icon: { SvgComponent: RefreshSVG, alignment: Button.iconAlignments.left },
    };
    return (
      <Button
        {...syncButtonProps}
        dataProps={{
          "data-testid": "disabled-connected-btn",
        }}
        disabled
      >
        Connected to {authProviderName}
      </Button>
    );
  };

  const renderSyncClassroom = () => {
    const authProviderName = getAuthProviderName(user.auth_type);
    const syncButtonProps = {
      flavor: Button.flavors.secondary,
      icon: { SvgComponent: RefreshSVG, alignment: Button.iconAlignments.left },
    };
    return (
      <>
        {syncing && (
          <LoadingSpinner
            dataProps={{ "data-testid": "classes-sync-loading-spinner" }}
            __cssFor={classSyncLoadingSpinner}
          />
        )}
        <Button
          {...syncButtonProps}
          ref={syncClassroomModalNewSyncClassButtonRef}
          dataProps={{
            "data-testid": "sync-classes-modal-btn",
          }}
          onClick={() => setSyncClassModalActive(true)}
          disabled={syncing}
        >
          {syncing ? "Syncing..." : `Sync from ${authProviderName}`}
        </Button>
        <ClassroomSyncModal
          modalProps={syncClassModalProps}
          setActive={setSyncClassModalActive}
          onRosterSyncStarted={onSyncClassroom}
          canSyncClassrooms={canSyncClassrooms}
        />
      </>
    );
  };

  const renderCreateClassroom = () => {
    return (
      <>
        <Button
          __cssFor={buttonContentAlignmentCSS}
          flavor={Button.flavors.primary}
          ref={createClassroomModalNewClassButtonRef}
          icon={{
            SvgComponent: PlusSVG,
            alignment: Button.iconAlignments.left,
          }}
          onClick={() => setCreateClassModalActive(true)}
        >
          New class
        </Button>
        <CreateNewClassroomModal
          createClassroomModalProps={createClassModalProps}
          handleCancel={() => setCreateClassModalActive(false)}
          submitCallback={onNewClassroom}
          isExternalActionLoading={isRedirecting}
          hideImportSection
        />
      </>
    );
  };

  const canRenderSaveRosterChoice = useMemo(() => {
    if (isMyRosterChoiceProvider) {
      return false;
    }

    if (canSyncClassrooms) {
      return hasBulkClassrooms || isIntegrationUser;
    }

    return isIntegrationUser && !hasBulkClassrooms;
  });

  if (isLoading) {
    return (
      <SettingsBasePage {...SETTINGS_BASE_PAGE_PROPS}>
        <LoadingSpinner />
      </SettingsBasePage>
    );
  }
  const Actions = (
    <div css={actionsContainerCss}>
      {canSyncClassrooms && isMyRosterChoiceProvider && renderSyncClassroom()}
      {canRenderSaveRosterChoice && renderSaveRosterChoice()}
      {!canSyncClassrooms &&
        !hasBulkClassrooms &&
        isMyRosterChoiceProvider &&
        renderConnectedToProvider()}
      {canCreateClassrooms && renderCreateClassroom()}
    </div>
  );

  const TableNotFound = (
    <DirectoryTable.NotFound
      title="Looking for classes?"
      subTitle="First, create or sync a new class and add students to it."
    />
  );

  const renderInfoBanners = () => {
    const isMissingGoogleScopes =
      errorRosterClassrooms &&
      errorRosterClassrooms?.statusCode === 403 &&
      errorRosterClassrooms
        ?.toJSON()
        .message.includes("not have sufficient Google scopes");

    return (
      <>
        {!canSyncClassrooms &&
          isMyRosterChoiceProvider &&
          !errorRosterClassrooms && (
            <Banner
              bannerType={Banner.types.info}
              body={`Not seeing all of your classes? Head back to
            ${getAuthProviderName(user.auth_type)} and log into Newsela from your other courses.`}
            />
          )}
        {!canSyncClassrooms &&
          isMyRosterChoiceProvider &&
          isMissingGoogleScopes && (
            <Banner
              bannerType={Banner.types.info}
              body="Not seeing your desired classes? You will need to re-login and authorize the required Google Classroom permissions."
              isDismissible={false}
              ctaProps={{
                text: "Re-login with Google",
                href: "/login/google-oauth2/?next=/account/classes/",
                openInNewTab: false,
              }}
              dataProps={{
                "data-testid": "re-login-google-auth-due-lack-of-scopes",
              }}
            />
          )}
        {showBannerRelayingStudentSearchBlock && (
          <Banner
            isCenter={false}
            bannerType={Banner.types.info}
            body={`Students in the following grades in your school don't have access to Newsela search or the homepage: ${getFormattedGradesWithStudentSearchDisabled()}. Students are only able to see articles assigned to them by teachers.`}
            isDismissible={false}
            __cssFor={bannerForRelayingStudentSearchBlockCss}
          />
        )}
      </>
    );
  };

  return (
    <SettingsBasePage
      {...SETTINGS_BASE_PAGE_PROPS}
      AlertComponent={renderAlertBanner()}
      actions={Actions}
      pageDescription={renderPageDescription()}
    >
      {renderInfoBanners()}
      <UserClassroomDirectoryTable
        TableNotFound={TableNotFound}
        showSearch={!user.roles.includes("parent")}
        classrooms={classrooms}
        count={count}
        isLoading={loadingClasses}
        controls={controls}
        fetchClassrooms={fetchClassrooms}
        pageSize={PAGE_SIZE}
      />
      <LearnAboutClassRoster hasBulkClassrooms={hasBulkClassrooms} />
    </SettingsBasePage>
  );
};

export default ClassesSettingsPage;
